import { decodeJwtClaims, isLaunchToken } from '../jwt'
import { brandKeyOf } from './brand-icons'
import { cleanMethod, forgetAccount, rememberAccount } from './recent-accounts'
import { hasAnySignInMark } from '../../view/lms-learner/signin-marker'

// What a successful sign-in leaves in this browser's recent accounts
// (recent-accounts.js), and the two notes that decide it.
//
// Every sign-in the admin finishes goes through signInAndRemember with the
// new session's token and how it signed in: the password step
// (login-email.js), single sign-on on its way back (/open/auth), the device
// code (login-device-modal.js) and an invitation's new password
// (invite-register.js). Nothing else records: a refresh, an organization
// switch or an impersonation redeem is not a sign-in.
//
// Never recorded:
//   - an impersonated session (its token carries impersonationEndsAt or
//     impersonatorLoginID), or any sign-in made in a tab that held one;
//   - a bounded LMS session's token;
//   - a token that names no email (a username sign-in: nothing to match it
//     by later);
//   - anyone, while "This is a shared device" is ticked: their entry is
//     removed instead.

const sessionStore = () => {
  try {
    return window.sessionStorage
  } catch (e) {
    return null
  }
}

// "This is a shared device -- don't remember me", for this browser session
// only: a session cookie (no expiry), so every tab of the browser sees it,
// single sign-on's round trip keeps it, and closing the browser ends it.
// Only "1" is ever written; the server ignores it.
export const SHARED_DEVICE_COOKIE = 'edxr-shared-device'

export const isSharedDevice = () => {
  try {
    return document.cookie.split(';').some(part => part.trim() === `${SHARED_DEVICE_COOKIE}=1`)
  } catch (e) {
    return false
  }
}

export const setSharedDevice = on => {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = on
      ? `${SHARED_DEVICE_COOKIE}=1; path=/; SameSite=Lax${secure}`
      : `${SHARED_DEVICE_COOKIE}=; path=/; SameSite=Lax; Max-Age=0${secure}`
  } catch (e) {}
}

// A sign-in made for an LMS launch (owner 2026-10-30) is never remembered:
// the learner's launch window (/lms/launch/<intent>) sends them to sign in
// with `redirect` (base64 of that path), and marks the tab first
// (signin-marker.js), which is what survives single sign-on's round trip.
// `redirect` is the sign-in page's own parameter; without one the address
// bar's is read (the device-code window sits on the same page).
export const LMS_LAUNCH_PREFIX = '/lms/launch/'

const currentRedirect = () => {
  try {
    return new URLSearchParams(window.location.search).get('redirect')
  } catch (e) {
    return null
  }
}

export const isLaunchSignIn = redirect => {
  try {
    const value = redirect === undefined ? currentRedirect() : redirect
    if (value && atob(value).startsWith(LMS_LAUNCH_PREFIX)) return true
  } catch (e) {}
  return hasAnySignInMark()
}

// Single sign-on leaves the page and comes back on /open/auth, which knows
// only the token. The connection it started with is noted here first
// (sessionStorage, this tab), and taken once on the way back. Older than an
// hour, it is not this sign-in's; any other sign-in drops it.
export const PENDING_SIGN_IN_KEY = 'edxr-pending-sign-in'
const PENDING_TTL_MS = 60 * 60 * 1000

export const notePendingSignIn = (method, { now = Date.now() } = {}) => {
  const clean = cleanMethod(method)
  if (!clean) return
  try {
    const store = sessionStore()
    if (store) store.setItem(PENDING_SIGN_IN_KEY, JSON.stringify({ method: clean, at: now }))
  } catch (e) {}
}

export const takePendingSignIn = ({ now = Date.now() } = {}) => {
  try {
    const store = sessionStore()
    if (!store) return null
    const raw = store.getItem(PENDING_SIGN_IN_KEY)
    store.removeItem(PENDING_SIGN_IN_KEY)
    if (!raw) return null
    const value = JSON.parse(raw)
    if (!value || typeof value.at !== 'number' || now - value.at > PENDING_TTL_MS || now < value.at) return null
    return cleanMethod(value.method)
  } catch (e) {
    return null
  }
}

// The single sign-on note for a connection (auth's provider link): its
// identifier and label, and a small brand key for its icon (never the SVG).
export const ssoMethod = providerLink => ({
  kind: 'sso',
  connection: providerLink
    ? { identifier: providerLink.identifier, displayName: providerLink.displayName, iconKey: brandKeyOf(providerLink) || undefined }
    : undefined
})

export const isImpersonated = claims => !!claims && (!!claims.impersonationEndsAt || claims.impersonatorLoginID != null)

// This tab's session as storage/auth.js keeps it (sessionStorage).
const tabAccessToken = () => {
  try {
    const store = sessionStore()
    return (store && store.getItem('accessToken')) || null
  } catch (e) {
    return null
  }
}

export const displayNameOf = claims => `${(claims && claims.firstName) || ''} ${(claims && claims.lastName) || ''}`.replace(/\s+/g, ' ').trim()

// authToken: what auth answered ({ accessToken, refreshToken }); only the
// access token's claims are read, and no token is ever kept. method: how
// this sign-in went. typed: what the person typed, removed too on a shared
// device. priorAccessToken: the tab's session before this sign-in replaced
// it (signInAndRemember reads it first); an impersonation there means
// nothing is recorded. sharedDevice: the page's own checkbox, when it has
// one (else the cookie). Answers 'remembered', 'forgotten' or 'skipped', and
// never throws: remembering must never get in the way of a sign-in.
export const recordSignIn = ({ authToken, method, typed, priorAccessToken, sharedDevice, launch } = {}) => {
  try {
    // A pending single sign-on note is this sign-in's or nobody's.
    if (!method || method.kind !== 'sso') takePendingSignIn()

    const accessToken = authToken && authToken.accessToken
    const claims = decodeJwtClaims(accessToken)
    if (!claims) return 'skipped'
    if (isLaunchToken(accessToken)) return 'skipped'
    const prior = priorAccessToken === undefined ? tabAccessToken() : priorAccessToken
    if (isImpersonated(claims) || isImpersonated(decodeJwtClaims(prior))) return 'skipped'

    const email = typeof claims.email === 'string' ? claims.email.trim() : ''
    const typedValue = typeof typed === 'string' ? typed.trim() : ''

    if (launch === true || isLaunchSignIn()) return 'skipped'

    if (sharedDevice === true || isSharedDevice()) {
      if (email) forgetAccount(email)
      if (typedValue && typedValue.toLowerCase() !== email.toLowerCase()) forgetAccount(typedValue)
      return 'forgotten'
    }

    if (!email) return 'skipped'

    rememberAccount({ email, name: displayNameOf(claims), method })
    return 'remembered'
  } catch (e) {
    return 'skipped'
  }
}

// The one way a sign-in hands over its tokens and is remembered: reads the
// tab's session first (an impersonation there means nothing is recorded),
// sets the new one (setAuthToken), then records it.
export const signInAndRemember = (setAuthToken, authToken, options = {}) => {
  const priorAccessToken = tabAccessToken()
  setAuthToken(authToken)
  return recordSignIn({ ...options, authToken, priorAccessToken })
}
